import { toast } from "sonner";

const DEFAULT_API_BASE = "https://api.dill-y.com";
const DEFAULT_CSRF_COOKIE_NAME = "dilly_csrf";

export const API_BASE: string =
  (import.meta as any).env?.VITE_API_BASE ?? DEFAULT_API_BASE;

export const CSRF_COOKIE_NAME: string =
  (import.meta as any).env?.VITE_CSRF_COOKIE_NAME ?? DEFAULT_CSRF_COOKIE_NAME;

function extractDetailMessage(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object") return null;
    const detail = (parsed as any).detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === "object" ? (d as any).msg : null))
        .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
      if (msgs.length > 0) return msgs.join("\n");
    }
  } catch {
    // not JSON
  }
  return null;
}

let lastForbiddenToastAt = 0;
function maybeShowForbiddenToast(res: Response) {
  if (res.status !== 403) return;
  const now = Date.now();
  // Debounce so a wave of parallel 403s only surfaces one toast.
  if (now - lastForbiddenToastAt < 800) return;
  lastForbiddenToastAt = now;

  res
    .clone()
    .text()
    .then((text) => {
      const detail = extractDetailMessage(text);
      toast.error(detail ?? "You don't have permission to perform this action.");
    })
    .catch(() => {
      toast.error("You don't have permission to perform this action.");
    });
}

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const parts = document.cookie.split(";").map((p) => p.trim());
  const prefix = `${encodeURIComponent(name)}=`;
  for (const p of parts) {
    if (p.startsWith(prefix)) {
      return decodeURIComponent(p.slice(prefix.length));
    }
  }
  return null;
}

export class BackendError extends Error {
  status: number;
  bodyText?: string;

  constructor(message: string, opts: { status: number; bodyText?: string }) {
    super(message);
    this.name = "BackendError";
    this.status = opts.status;
    this.bodyText = opts.bodyText;
  }
}

type BackendFetchInit = RequestInit & {
  retryOn401?: boolean;
};

export async function refreshSession(): Promise<boolean> {
  const csrf = getCookie(CSRF_COOKIE_NAME) ?? "";
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: csrf ? { "x-csrf-token": csrf } : {},
  });
  return res.status === 204;
}

export async function backendFetch(
  path: string,
  init: BackendFetchInit = {}
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const { retryOn401 = true, ...requestInit } = init;

  const res = await fetch(url, {
    credentials: "include",
    ...requestInit,
  });

  if (res.status !== 401 || !retryOn401) {
    maybeShowForbiddenToast(res);
    return res;
  }

  // Try to refresh once, then retry request once.
  const refreshed = await refreshSession();
  if (!refreshed) {
    // Kick user to auth page on unrecoverable 401
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
      window.location.href = "/auth";
    }
    return res;
  }

  const retryRes = await fetch(url, {
    credentials: "include",
    ...requestInit,
  });

  if (retryRes.status === 401) {
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
      window.location.href = "/auth";
    }
  }

  maybeShowForbiddenToast(retryRes);
  return retryRes;
}

export async function backendJson<T>(
  path: string,
  init: BackendFetchInit = {}
): Promise<{ response: Response; data: T | null }> {
  const response = await backendFetch(path, init);
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return { response, data: null };
  }
  const data = (await response.json()) as T;
  return { response, data };
}
